import React, { useState, useEffect } from 'react';

const CSVConfigDialog = ({ isOpen, onClose, csvInfo, onSave }) => {
  const [selectedColumns, setSelectedColumns] = useState({
    x: '',
    y: '',
    label: ''
  });
  const [selectedCRS, setSelectedCRS] = useState('EPSG:4326');
  const [hasHeaders, setHasHeaders] = useState(true);
  const [errors, setErrors] = useState({});

  // Common CRS options
  const crsOptions = [
    { value: 'EPSG:4326', label: 'WGS 84 (EPSG:4326)' },
    { value: 'EPSG:3857', label: 'Web Mercator (EPSG:3857)' },
    { value: 'EPSG:2154', label: 'RGF93 / Lambert-93 (EPSG:2154)' },
    { value: 'EPSG:27700', label: 'British National Grid (EPSG:27700)' },
    { value: 'EPSG:32633', label: 'WGS 84 / UTM zone 33N (EPSG:32633)' },
    { value: 'EPSG:32634', label: 'WGS 84 / UTM zone 34N (EPSG:32634)' },
    { value: 'EPSG:32635', label: 'WGS 84 / UTM zone 35N (EPSG:32635)' }
  ];

  // Auto-detect likely columns when dialog opens or headers setting changes
  useEffect(() => {
    if (isOpen && csvInfo?.columns) {
      const autoDetected = { x: '', y: '', label: '' };

      if (hasHeaders && csvInfo.potentialColumnNames) {
        // Auto-detect using actual column names from first row
        const columns = csvInfo.potentialColumnNames;
        
        // Auto-detect X column (longitude, x, lon, lng, easting)
        const xCandidates = ['longitude', 'lon', 'lng', 'x', 'easting', 'long'];
        for (const candidate of xCandidates) {
          const match = columns.find(col => 
            col && col.toLowerCase().includes(candidate.toLowerCase())
          );
          if (match) {
            autoDetected.x = match;
            break;
          }
        }

        // Auto-detect Y column (latitude, y, lat, northing)
        const yCandidates = ['latitude', 'lat', 'y', 'northing'];
        for (const candidate of yCandidates) {
          const match = columns.find(col => 
            col && col.toLowerCase().includes(candidate.toLowerCase())
          );
          if (match) {
            autoDetected.y = match;
            break;
          }
        }

        // Auto-detect label column (name, title, label, description)
        const labelCandidates = ['name', 'title', 'label', 'description', 'desc'];
        for (const candidate of labelCandidates) {
          const match = columns.find(col => 
            col && col.toLowerCase().includes(candidate.toLowerCase())
          );
          if (match) {
            autoDetected.label = match;
            break;
          }
        }
      }

      setSelectedColumns(autoDetected);
      setErrors({});
    }
  }, [isOpen, csvInfo, hasHeaders]);

  const handleColumnChange = (columnType, value) => {
    setSelectedColumns(prev => ({
      ...prev,
      [columnType]: value
    }));
    
    // Clear error for this field
    if (errors[columnType]) {
      setErrors(prev => ({
        ...prev,
        [columnType]: null
      }));
    }
  };

  const validateConfiguration = () => {
    const newErrors = {};
    
    if (!selectedColumns.x) {
      newErrors.x = 'X coordinate column is required';
    }
    if (!selectedColumns.y) {
      newErrors.y = 'Y coordinate column is required';
    }
    
    // Check if X and Y columns are the same
    if (selectedColumns.x && selectedColumns.y && selectedColumns.x === selectedColumns.y) {
      newErrors.x = 'X and Y columns must be different';
      newErrors.y = 'X and Y columns must be different';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateConfiguration()) {
      console.log('CSV Config - Saving with hasHeaders:', hasHeaders);
      console.log('Selected columns:', selectedColumns);
      onSave({
        columns: selectedColumns,
        crs: selectedCRS,
        hasHeaders: hasHeaders
      });
    }
  };

  const getColumnPreview = (columnName) => {
    if (!csvInfo || !columnName) return [];
    
    console.log('Getting preview for column:', columnName, 'hasHeaders:', hasHeaders);
    console.log('csvInfo.columns:', csvInfo.columns);
    console.log('csvInfo.potentialColumnNames:', csvInfo.potentialColumnNames);
    
    // Find the data key to use
    let dataKey;
    let sampleData;
    
    if (hasHeaders && csvInfo.potentialColumnNames) {
      // In headers mode, columnName is the actual column name from first row
      // Find its index and map to the csv-parser key
      const columnIndex = csvInfo.potentialColumnNames.indexOf(columnName);
      if (columnIndex >= 0 && csvInfo.columns[columnIndex]) {
        dataKey = csvInfo.columns[columnIndex];
        sampleData = csvInfo.sampleData || [];
      }
    } else {
      // In non-headers mode, columnName is "Column X", need to map to csv-parser key from rawSampleData
      console.log('Non-headers mode, columnName:', columnName);
      const columnMatch = columnName.match(/Column (\d+)/);
      console.log('Column match result:', columnMatch);
      if (columnMatch) {
        const columnIndex = parseInt(columnMatch[1]) - 1;
        console.log('Column index:', columnIndex);
        // For rawSampleData, the keys are '0', '1', '2', etc. (not '_0', '_1', '_2')
        dataKey = columnIndex.toString();
        sampleData = csvInfo.rawSampleData || []; // Use all rawSampleData including first row as data
        console.log('Set dataKey to:', dataKey, 'rawSampleData sample:', csvInfo.rawSampleData?.[0]);
      }
    }
    
    console.log('Using dataKey:', dataKey, 'sampleData length:', sampleData?.length);
    
    if (!dataKey || !sampleData) return [];
    
    const values = sampleData
      .slice(0, 3)
      .map(row => {
        const value = row[dataKey];
        console.log(`Row data for key ${dataKey}:`, value, 'Full row:', row);
        return value;
      })
      .filter(val => val !== undefined && val !== null && val !== '');
    
    console.log('Final preview values:', values);
    return values;
  };

  const getAvailableColumns = () => {
    if (!csvInfo?.columns) return [];
    
    if (hasHeaders && csvInfo.potentialColumnNames) {
      // Use the actual first row values as column names
      return csvInfo.potentialColumnNames;
    } else {
      // Generate generic column names when no headers
      return csvInfo.columns.map((_, index) => `Column ${index + 1}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="csv-config-overlay">
      <div className="csv-config-dialog">
        <div className="csv-config-header">
          <h3>Configure CSV Import</h3>
          <button 
            className="csv-config-close"
            onClick={onClose}
            title="Close dialog"
          >
            ×
          </button>
        </div>

        <div className="csv-config-content">
          <div className="csv-info-section">
            <h4>File Information</h4>
            <div className="csv-info-grid">
              <div className="csv-info-item">
                <span className="csv-info-label">Delimiter:</span>
                <span className="csv-info-value">
                  {csvInfo?.delimiter === '\t' ? 'Tab' : 
                   csvInfo?.delimiter === ';' ? 'Semicolon' :
                   csvInfo?.delimiter === '|' ? 'Pipe' :
                   csvInfo?.delimiter || 'Comma'}
                </span>
              </div>
              <div className="csv-info-item">
                <span className="csv-info-label">Rows:</span>
                <span className="csv-info-value">{csvInfo?.rowCount || 0}</span>
              </div>
              <div className="csv-info-item">
                <span className="csv-info-label">Columns:</span>
                <span className="csv-info-value">{csvInfo?.columns?.length || 0}</span>
              </div>
            </div>
          </div>

          <div className="csv-config-section">
            <h4>Data Format</h4>
            <div className="csv-headers-option">
              <label className="csv-checkbox-label">
                <input
                  type="checkbox"
                  checked={hasHeaders}
                  onChange={(e) => setHasHeaders(e.target.checked)}
                  className="csv-checkbox"
                />
                First row contains column headers
              </label>
            </div>
          </div>

          <div className="csv-config-section">
            <h4>Column Mapping</h4>
            <p className="csv-config-description">
              Select which columns contain coordinate data and optional labels.
            </p>

            <div className="csv-column-mapping">
              <div className="csv-mapping-row">
                <label className="csv-mapping-label">
                  X Coordinate (Longitude) *
                </label>
                <select
                  value={selectedColumns.x}
                  onChange={(e) => handleColumnChange('x', e.target.value)}
                  className={`csv-mapping-select ${errors.x ? 'error' : ''}`}
                >
                  <option value="">Select column...</option>
                  {getAvailableColumns().map((col, index) => (
                    <option key={index} value={col}>{col}</option>
                  ))}
                </select>
                {selectedColumns.x && (
                  <div className="csv-column-preview">
                    Preview: {getColumnPreview(selectedColumns.x).join(', ')}
                  </div>
                )}
                {errors.x && <div className="csv-error">{errors.x}</div>}
              </div>

              <div className="csv-mapping-row">
                <label className="csv-mapping-label">
                  Y Coordinate (Latitude) *
                </label>
                <select
                  value={selectedColumns.y}
                  onChange={(e) => handleColumnChange('y', e.target.value)}
                  className={`csv-mapping-select ${errors.y ? 'error' : ''}`}
                >
                  <option value="">Select column...</option>
                  {getAvailableColumns().map((col, index) => (
                    <option key={index} value={col}>{col}</option>
                  ))}
                </select>
                {selectedColumns.y && (
                  <div className="csv-column-preview">
                    Preview: {getColumnPreview(selectedColumns.y).join(', ')}
                  </div>
                )}
                {errors.y && <div className="csv-error">{errors.y}</div>}
              </div>

              <div className="csv-mapping-row">
                <label className="csv-mapping-label">
                  Label Column
                  <span className="csv-optional">(optional)</span>
                </label>
                <select
                  value={selectedColumns.label}
                  onChange={(e) => handleColumnChange('label', e.target.value)}
                  className="csv-mapping-select"
                >
                  <option value="">None</option>
                  {getAvailableColumns().map((col, index) => (
                    <option key={index} value={col}>{col}</option>
                  ))}
                </select>
                {selectedColumns.label && (
                  <div className="csv-column-preview">
                    Preview: {getColumnPreview(selectedColumns.label).join(', ')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="csv-config-section">
            <h4>Coordinate Reference System</h4>
            <p className="csv-config-description">
              Select the coordinate system of your data.
            </p>
            
            <select
              value={selectedCRS}
              onChange={(e) => setSelectedCRS(e.target.value)}
              className="csv-crs-select"
            >
              {crsOptions.map(crs => (
                <option key={crs.value} value={crs.value}>{crs.label}</option>
              ))}
            </select>
          </div>

          {csvInfo?.sampleData && csvInfo.sampleData.length > 0 && (
            <div className="csv-config-section">
              <h4>Data Preview</h4>
              <div className="csv-preview-table">
                <table>
                  <thead>
                    <tr>
                      {getAvailableColumns().map((col, index) => (
                        <th key={index}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      console.log('Rendering preview table, hasHeaders:', hasHeaders);
                      console.log('csvInfo.sampleData:', csvInfo.sampleData);
                      console.log('csvInfo.rawSampleData:', csvInfo.rawSampleData);
                      
                      // For headers mode: use sampleData (already excludes header)
                      // For non-headers mode: use rawSampleData (all rows are data)
                      let dataRows;
                      let columnKeys;
                      
                      if (hasHeaders) {
                        dataRows = csvInfo.sampleData || [];
                        columnKeys = csvInfo.columns; // Use '_0', '_1', '_2' keys
                      } else {
                        // When no headers, show all rows from rawSampleData as data
                        dataRows = csvInfo.rawSampleData || [];
                        columnKeys = csvInfo.columns.map((_, index) => index.toString()); // Use '0', '1', '2' keys
                      }
                      
                      console.log('Using dataRows:', dataRows, 'columnKeys:', columnKeys);
                      
                      return dataRows.slice(0, 3).map((row, index) => (
                        <tr key={index}>
                          {columnKeys.map((colKey, colIndex) => (
                            <td key={colIndex}>{row[colKey] || ''}</td>
                          ))}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="csv-config-footer">
          <button 
            className="btn-csv-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="btn-csv-save"
            onClick={handleSave}
            disabled={!selectedColumns.x || !selectedColumns.y}
          >
            Import CSV
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSVConfigDialog;